import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api, { BACKEND_URL } from '../../lib/api';

const ProfitLoss = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchPL();
  }, [navigate]);

  const fetchPL = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const r = await api.get(`/accounts/profit-loss?${params}`);
      setData(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const exportPDF = () => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    const token = localStorage.getItem('token');
    window.open(`${BACKEND_URL}/api/accounts/export/profit-loss?${params}&token=${token}`, '_blank');
  };

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="profit-loss-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="mb-6 flex justify-between items-center">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="pl-title">Profit & Loss</h1>
            {data && <button onClick={exportPDF} className="flex items-center gap-1 text-[#1E40AF] font-medium hover:underline" data-testid="export-pl-pdf"><Download className="w-4 h-4" /> Export PDF</button>}
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div><label className="block text-sm font-medium mb-1">Start Date</label><input type="date" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
              <div><label className="block text-sm font-medium mb-1">End Date</label><input type="date" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={endDate} onChange={e => setEndDate(e.target.value)} /></div>
              <button onClick={fetchPL} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white hover:opacity-90 rounded-sm px-4 py-2 font-medium" data-testid="fetch-pl-button">Refresh</button>
            </div>
          </div>

          {loading ? <div className="text-center py-12">Loading...</div> : !data ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center text-[#6b7280]">No data</div>
          ) : (
            <div className="space-y-4">
              {/* Revenue */}
              <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm">
                <div className="px-4 py-3 border-b border-[#e5e7eb] bg-[#EFF6FF]"><h2 className="font-semibold text-[#1E40AF]">Revenue</h2></div>
                <table className="w-full text-sm">
                  <tbody>
                    {data.revenue.length === 0 ? <tr><td className="py-4 px-4 text-center text-[#6b7280]" colSpan="2">No revenue</td></tr> : data.revenue.map((r, i) => (
                      <tr key={i} className="border-t border-[#e5e7eb]"><td className="py-2 px-4">{r.code} - {r.name}</td><td className="py-2 px-4 text-right font-medium">Rs. {r.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#f9fafb] font-semibold"><tr><td className="py-2 px-4">Total Revenue</td><td className="py-2 px-4 text-right text-[#1E40AF]">Rs. {data.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr></tfoot>
                </table>
              </div>

              {/* COGS */}
              <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm">
                <div className="px-4 py-3 border-b border-[#e5e7eb] bg-[#FFF7ED]"><h2 className="font-semibold text-[#92400E]">Cost of Goods Sold (COGS)</h2></div>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-t border-[#e5e7eb]">
                      <td className="py-2 px-4 text-[#6b7280]">Direct cost of products sold (quantity × unit cost)</td>
                      <td className="py-2 px-4 text-right font-medium text-[#92400E]">Rs. {(data.cogs || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    </tr>
                  </tbody>
                  <tfoot className="bg-[#FFF7ED] font-semibold">
                    <tr>
                      <td className="py-2 px-4">Gross Profit <span className="text-xs font-normal text-[#6b7280]">(Revenue − COGS)</span></td>
                      <td className={`py-2 px-4 text-right ${data.gross_profit >= 0 ? 'text-[#1E40AF]' : 'text-[#991B1B]'}`}>Rs. {data.gross_profit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Operating Expenses */}
              <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm">
                <div className="px-4 py-3 border-b border-[#e5e7eb] bg-[#FEF2F2]"><h2 className="font-semibold text-[#991B1B]">Operating Expenses</h2></div>
                <table className="w-full text-sm">
                  <tbody>
                    {data.expenses.length === 0 ? <tr><td className="py-4 px-4 text-center text-[#6b7280]" colSpan="2">No expenses</td></tr> : data.expenses.map((e, i) => (
                      <tr key={i} className="border-t border-[#e5e7eb]"><td className="py-2 px-4">{e.code} - {e.name}</td><td className="py-2 px-4 text-right font-medium">Rs. {e.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#f9fafb] font-semibold"><tr><td className="py-2 px-4">Total Operating Expenses</td><td className="py-2 px-4 text-right text-[#991B1B]">Rs. {data.total_expenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr></tfoot>
                </table>
              </div>

              {/* Summary */}
              <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                  <div className="p-4 border border-[#e5e7eb] rounded-sm">
                    <p className="text-xs text-[#6b7280] mb-1 uppercase tracking-wide">Total Revenue</p>
                    <p className="text-xl font-bold text-[#1E40AF]">Rs. {data.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="p-4 border border-[#FDE68A] rounded-sm bg-[#FFFBEB]">
                    <p className="text-xs text-[#6b7280] mb-1 uppercase tracking-wide">Less: COGS</p>
                    <p className="text-xl font-bold text-[#92400E]">Rs. {(data.cogs || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="p-4 border border-[#e5e7eb] rounded-sm">
                    <p className="text-xs text-[#6b7280] mb-1 uppercase tracking-wide">Gross Profit</p>
                    <p className={`text-xl font-bold ${data.gross_profit >= 0 ? 'text-[#1E40AF]' : 'text-[#991B1B]'}`}>Rs. {data.gross_profit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="p-4 border-2 border-[#1E40AF] rounded-sm bg-[#EFF6FF]">
                    <p className="text-xs text-[#6b7280] mb-1 uppercase tracking-wide">Net Profit</p>
                    <p className={`text-xl font-bold ${data.net_profit >= 0 ? 'text-[#1E40AF]' : 'text-[#991B1B]'}`} data-testid="net-profit">Rs. {data.net_profit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProfitLoss;
